import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import { getProfile, putProfile } from '../../lib/jobsApi';
import { getCompany, patchCompany } from '../../lib/companyApi';
import type { Company } from '../../lib/companyApi';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../lib/api';
import { hrErrorMessage } from '../../lib/tenancyErrorCopy';

/*
 * /hr/profile — two independent records on one page.
 *
 *  · "Your details" is hr_profiles: title and phone, the person. Anyone may edit their own.
 *  · "Company" is the companies row: name, website, description — the fields the AI reads when
 *    it drafts tasks and the name candidates see on the public board. An OWNER edits it; a
 *    MEMBER sees it read-only, because one member renaming the shared tenant is not a
 *    per-person setting.
 *
 * The split is not cosmetic. Those three company fields used to be columns on hr_profiles, one
 * copy per recruiter, so two people at the same employer could disagree about its name and the
 * public board would show whichever of them posted the job. They moved to `companies` in N3, and
 * PUT /api/profile's hr schema is .strict() — it now REFUSES a payload carrying them
 * (server/src/routes/profile.ts:10). This page was sending them until this commit.
 *
 * Two records means two forms and two save buttons, deliberately: they hit different endpoints
 * with different authorisation (PATCH /api/company is owner-only, 403 insufficient_company_role
 * for a member), and one button that half-succeeds is worse than two that each say what they did.
 *
 * A failed load must never fall through to an empty editable form — a Save would overwrite the
 * stored record with blanks — so the page renders its own error state instead.
 */

/* The server's own cap, from companyPatchSchema (server/src/routes/company.ts). Task 500 aligned
 * it DOWN to 4000 to match the admin edit form, which is what makes a recruiter's description
 * re-submittable by an admin. Typing to a higher ceiling here would just earn a 400. */
const DESCRIPTION_MAX = 4000;

const Spinner: React.FC<{ label: string }> = ({ label }) => (
  <>
    <span
      aria-hidden="true"
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={{ borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }}
    />
    <span>{label}</span>
  </>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-10 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>Something went wrong</span>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-6">Try again</button>
  </div>
);

const Field: React.FC<{
  id: string; label: string; value: string; error?: string; maxLength: number;
  type?: string; placeholder?: string; rows?: number;
  onChange: (value: string) => void;
}> = ({ id, label, value, error, maxLength, type = 'text', placeholder, rows, onChange }) => (
  <div className="space-y-2">
    <label htmlFor={id} className="block text-sm font-medium text-ink">{label}</label>
    {rows ? (
      <textarea
        id={id} value={value} rows={rows} maxLength={maxLength} placeholder={placeholder}
        aria-invalid={!!error} className="input resize-none"
        style={error ? { borderColor: 'var(--color-error)' } : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input
        id={id} type={type} value={value} maxLength={maxLength} placeholder={placeholder}
        aria-invalid={!!error} className="input"
        style={error ? { borderColor: 'var(--color-error)' } : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
    {error && <p role="alert" className="text-xs text-error font-medium">{error}</p>}
  </div>
);

const ReadOnlyRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <span className="label-editorial block mb-1">{label}</span>
    <p className="text-ink whitespace-pre-line">{value || '—'}</p>
  </div>
);

/* Which of the three the SAVED record is still missing — read off `company`, not off the form,
 * because completeness is a property of what is stored. Mirrors isCompanyComplete's own
 * `.trim() !== ''` test on each field (server/src/routes/company.ts). */
function missingCompanyFields(company: Company | null): string[] {
  if (!company) return [];
  const missing: string[] = [];
  if (company.name.trim() === '') missing.push('name');
  if (company.website.trim() === '') missing.push('website');
  if (company.description.trim() === '') missing.push('description');
  return missing;
}

const HRProfile: React.FC = () => {
  const { user } = useAuth();

  const [person, setPerson] = useState({ title: '', phone: '' });
  const [personErrors, setPersonErrors] = useState<Record<string, string>>({});
  const [personBusy, setPersonBusy] = useState(false);

  const [company, setCompany] = useState<Company | null>(null);
  const [companyForm, setCompanyForm] = useState({ name: '', website: '', description: '' });
  const [companyErrors, setCompanyErrors] = useState<Record<string, string>>({});
  const [companyBusy, setCompanyBusy] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const [profileRes, companyRes] = await Promise.all([getProfile(), getCompany()]);
        if (cancelled) return;
        if (profileRes.profile) {
          setPerson({ title: profileRes.profile.title || '', phone: profileRes.profile.phone || '' });
        }
        setCompany(companyRes.company);
        setCompanyForm({
          name: companyRes.company.name,
          website: companyRes.company.website,
          description: companyRes.company.description,
        });
      } catch (err) {
        if (!cancelled) setLoadError(hrErrorMessage(err, 'Check your connection and try again.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadNonce]);

  const savePerson = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (personBusy) return;
    setPersonBusy(true);
    try {
      await putProfile({ title: person.title, phone: person.phone });
      setPersonErrors({});
      toast.success('Your details saved.');
    } catch (err) {
      if (err instanceof ApiError && err.fields) setPersonErrors(err.fields);
      else toast.error(hrErrorMessage(err, 'Failed to save your details.'));
    } finally { setPersonBusy(false); }
  }, [person, personBusy]);

  const saveCompany = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (companyBusy) return;
    setCompanyBusy(true);
    try {
      // All three every time. PATCH /api/company diffs against the stored row and writes nothing
      // when nothing moved — no updated_at bump, no audit row — so re-sending an unchanged field
      // is a genuine no-op rather than a silent touch.
      const res = await patchCompany(companyForm);
      setCompany(res.company);
      setCompanyErrors({});
      toast.success('Company profile saved.');
    } catch (err) {
      if (err instanceof ApiError && err.fields) setCompanyErrors(err.fields);
      else toast.error(hrErrorMessage(err, 'Failed to save the company profile.'));
    } finally { setCompanyBusy(false); }
  }, [companyForm, companyBusy]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto" role="status">
        <span className="label-editorial block mb-3">Profile</span>
        <span aria-hidden="true" className="velaigal-loader-rule w-12 block" />
        <p className="mt-4 text-sm text-ink-tertiary">Loading your profile.</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-2xl mx-auto">
        <span className="label-editorial block mb-3">Profile</span>
        <ErrorBlock message={loadError} onRetry={() => setLoadNonce((n) => n + 1)} />
      </div>
    );
  }

  const isOwner = company?.companyRole === 'owner';
  const missing = missingCompanyFields(company);

  return (
    <div className="max-w-2xl mx-auto">
      <span className="label-editorial block mb-3">Profile</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">Your profile</h1>

      <div className="bg-paper border border-hairline rounded-md p-6 mb-8">
        <span className="label-editorial block mb-4">Account</span>
        <div className="space-y-3">
          <ReadOnlyRow label="Name" value={user?.name ?? ''} />
          <ReadOnlyRow label="Email" value={user?.email ?? ''} />
        </div>
      </div>

      <form onSubmit={savePerson} className="space-y-5 mb-12">
        <span className="label-editorial block">Your details</span>
        <Field id="title" label="Your title" value={person.title} error={personErrors.title}
          maxLength={200} placeholder="e.g., Hiring Manager"
          onChange={(v) => setPerson((p) => ({ ...p, title: v }))} />
        <Field id="phone" label="Phone" value={person.phone} error={personErrors.phone}
          maxLength={40} type="tel" placeholder="e.g., +91 90000 00000"
          onChange={(v) => setPerson((p) => ({ ...p, phone: v }))} />
        <button type="submit" className="btn-primary" disabled={personBusy}>
          {personBusy ? <Spinner label="Saving…" /> : 'Save your details'}
        </button>
      </form>

      <div className="pt-8 border-t border-hairline">
        <span className="label-editorial block mb-3">Company</span>
        <p className="text-sm text-ink-secondary leading-relaxed mb-6">
          Name, website and description are what the AI reads when it drafts your tasks, and the
          company name candidates see on the public board. All three must be filled in before a
          job can be generated or published.
        </p>

        {company?.complete ? (
          <div className="flex items-center gap-2 mb-6 p-3 bg-paper rounded-md border border-hairline">
            <Check size={14} color="var(--color-success)" className="flex-shrink-0" />
            <span className="text-xs text-ink font-medium">Company profile complete</span>
          </div>
        ) : (
          /* The counterpart to the tick, and the reason this page can carry the incomplete state
           * on its own: CompanyIncompleteNotice deliberately renders nothing on /hr/profile, so
           * without this the one screen that can fix it would be the one screen that never
           * mentions it. The label is saffron rather than error red — nothing has failed. */
          <div className="mb-6 p-3 bg-paper rounded-md border border-hairline">
            <span className="label-editorial" style={{ color: 'var(--color-saffron)' }}>Incomplete</span>
            <p className="mt-2 text-xs text-ink font-medium">
              {missing.length > 0
                /* isCompanyComplete folds `status` in as well as the three text fields, so an
                 * empty `missing` with complete === false means the company was suspended between
                 * the session load that let this page render and its own getCompany() — rare, but
                 * saying "still missing the ." would be a lie about a filled-in record. */
                ? `Still missing the ${missing.join(', ')}.`
                : 'This company is not active, so it counts as incomplete whatever these fields say.'}
            </p>
            <p className="mt-1 text-xs text-ink-tertiary">
              {isOwner
                ? 'Publishing a job and generating its tasks both stay unavailable until all three are filled in.'
                : 'Only a company owner can fill these in. Publishing a job and generating its tasks stay unavailable until they do.'}
            </p>
          </div>
        )}

        {isOwner ? (
          <form onSubmit={saveCompany} className="space-y-5">
            <Field id="companyName" label="Company name" value={companyForm.name} error={companyErrors.name}
              maxLength={200} placeholder="Your company's official name"
              onChange={(v) => setCompanyForm((c) => ({ ...c, name: v }))} />
            <Field id="companyWebsite" label="Company website" value={companyForm.website} error={companyErrors.website}
              maxLength={300} placeholder="https://example.com"
              onChange={(v) => setCompanyForm((c) => ({ ...c, website: v }))} />
            <Field id="companyDescription" label="What the company builds" value={companyForm.description}
              error={companyErrors.description} maxLength={DESCRIPTION_MAX} rows={6}
              placeholder="Two or three sentences on what you build and for whom…"
              onChange={(v) => setCompanyForm((c) => ({ ...c, description: v }))} />
            <p className="text-xs text-ink-tertiary">{companyForm.description.length}/{DESCRIPTION_MAX}</p>
            <button type="submit" className="btn-primary" disabled={companyBusy}>
              {companyBusy ? <Spinner label="Saving…" /> : 'Save company profile'}
            </button>
          </form>
        ) : (
          <div className="bg-paper border border-hairline rounded-md p-6 space-y-4">
            <ReadOnlyRow label="Company name" value={company?.name ?? ''} />
            <ReadOnlyRow label="Company website" value={company?.website ?? ''} />
            <ReadOnlyRow label="What the company builds" value={company?.description ?? ''} />
            <p className="text-xs text-ink-tertiary pt-2 border-t border-hairline">
              Only a company owner can change these.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HRProfile;
